import React, { useState, useRef, useEffect } from "react";
import VectorGhost from "./VectorGhost";

export interface FlyingGhostProps {
  id?: number;
  top: number | string;
  delay?: number | string;
  size?: number;
  duration?: number;
  direction?: number; // 1 for right to left, -1 for left to right
  color?: string;
  lives?: number; // số mạng (1~5), chỉ có tác dụng khi isGameActive
  rainbow?: boolean; // ma hiếm có bóng mờ 7 màu -> nhân điểm
  rainbowMultiplier?: number; // hệ số nhân điểm khi rainbow (mặc định 10)
  isGameActive?: boolean;
  onComplete?: (id: number) => void;
  onCatch?: (id: number, points: number) => void;
}

const HIT_COLOR = "#FFC896"; // cam nhạt khi trúng đòn

/* -------------------- Hiệu ứng âm thanh đơn giản (Web Audio, không cần file) -------------------- */
let audioCtx: AudioContext | null = null;
// Trạng thái bật/tắt âm thanh (chia sẻ toàn cục cho mọi ghost)
let soundMuted = false;

export const setGhostSoundMuted = (muted: boolean) => {
  soundMuted = muted;
};

// -----------------------------------------------------------------------------
// HTML5 Audio (WAV Data URI) Engine - phát âm thanh kể cả khi iPhone bật chế độ im lặng
// -----------------------------------------------------------------------------

function generateWavDataUri(
  duration: number,
  generator: (t: number, index: number, total: number) => number,
  sampleRate = 22050
): string {
  if (typeof window === "undefined") return "";
  const numSamples = Math.floor(sampleRate * duration);
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);

  function writeString(offset: number, string: string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  writeString(0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // Mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, numSamples * 2, true);

  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const sample = generator(t, i, numSamples);
    const s = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return "data:audio/wav;base64," + btoa(binary);
}

class SoundPool {
  private pool: HTMLAudioElement[] = [];
  private index = 0;

  constructor(getUri: () => string, size = 4) {
    if (typeof window === "undefined") return;
    try {
      const uri = getUri();
      if (!uri) return;
      for (let i = 0; i < size; i++) {
        const audio = new Audio(uri);
        audio.preload = "auto";
        audio.volume = 0.35; // Âm lượng vừa phải, êm tai
        audio.setAttribute("playsinline", "true");
        audio.setAttribute("webkit-playsinline", "true");
        this.pool.push(audio);
      }
    } catch {
      /* ignore */
    }
  }

  play() {
    if (soundMuted || this.pool.length === 0) return;
    const audio = this.pool[this.index];
    this.index = (this.index + 1) % this.pool.length;
    try {
      audio.currentTime = 0;
      audio.volume = 0.35;
      audio.play().catch(() => {});
    } catch {
      /* ignore */
    }
  }
}

let hitPool: SoundPool | null = null;
let catchPool: SoundPool | null = null;
let rainbowPool: SoundPool | null = null;

const getHitPool = () => {
  if (!hitPool && typeof window !== "undefined") {
    hitPool = new SoundPool(
      () =>
        generateWavDataUri(
          0.1,
          (t, i, total) => {
            const progress = i / total;
            const freq = 420 * Math.pow(220 / 420, progress);
            const phase = 2 * Math.PI * freq * t;
            const tri = (2 / Math.PI) * Math.asin(Math.sin(phase));
            return tri * (1 - progress) * 0.5;
          },
          22050
        ),
      4
    );
  }
  return hitPool;
};

const getCatchPool = () => {
  if (!catchPool && typeof window !== "undefined") {
    catchPool = new SoundPool(
      () =>
        generateWavDataUri(
          0.16,
          (t, i, total) => {
            const progress = i / total;
            const freq = 520 * Math.pow(980 / 520, progress);
            const phase = 2 * Math.PI * freq * t;
            const tri = (2 / Math.PI) * Math.asin(Math.sin(phase));
            return tri * (1 - Math.pow(progress, 1.4)) * 0.55;
          },
          22050
        ),
      4
    );
  }
  return catchPool;
};

const getRainbowPool = () => {
  if (!rainbowPool && typeof window !== "undefined") {
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    rainbowPool = new SoundPool(
      () =>
        generateWavDataUri(
          0.45,
          (t) => {
            let sample = 0;
            notes.forEach((f, idx) => {
              const startT = idx * 0.065;
              const noteDur = 0.14;
              if (t >= startT && t <= startT + noteDur) {
                const relT = t - startT;
                const progress = relT / noteDur;
                const phase = 2 * Math.PI * f * relT;
                const tri = (2 / Math.PI) * Math.asin(Math.sin(phase));
                sample += tri * (1 - progress) * 0.3;
              }
            });
            return sample;
          },
          22050
        ),
      2
    );
  }
  return rainbowPool;
};

export const initOrUnlockAudio = () => {
  if (soundMuted || typeof window === "undefined") return;
  getHitPool();
  getCatchPool();
  getRainbowPool();

  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === "suspended") {
      audioCtx.resume().catch(() => {});
    }
  } catch {
    /* ignore */
  }
};

const getAudioCtx = (): AudioContext | null => {
  if (soundMuted) return null;
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === "suspended") {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
};

// Phát 1 nốt đơn giản qua Web Audio API
const playTone = (
  ctx: AudioContext,
  freqStart: number,
  freqEnd: number,
  duration: number,
  type: OscillatorType = "triangle",
  gain = 0.09,
) => {
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  const now = ctx.currentTime && ctx.currentTime > 0 ? ctx.currentTime : 0.001;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(Math.max(20, freqStart), now);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, freqEnd), now + duration);
  g.gain.setValueAtTime(gain, now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + duration + 0.02);
};

// Ghost bị đánh nhưng còn mạng: âm "pop" giòn, vừa tai
const playHitSound = () => {
  getHitPool()?.play();
  const ctx = getAudioCtx();
  if (ctx) playTone(ctx, 420, 220, 0.1, "triangle", 0.08);
};

// Ghost chết (thường): âm "pặc" vút lên vui tai
const playCatchSound = () => {
  getCatchPool()?.play();
  const ctx = getAudioCtx();
  if (ctx) playTone(ctx, 520, 980, 0.16, "triangle", 0.09);
};

// Ghost cầu vồng chết: chuỗi nốt leo thang lấp lánh
const playRainbowCatchSound = () => {
  getRainbowPool()?.play();
  const ctx = getAudioCtx();
  if (ctx) {
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5]; // C5 E5 G5 C6 E6
    notes.forEach((f, i) => {
      setTimeout(() => playTone(ctx, f, f * 1.05, 0.14, "triangle", 0.08), i * 70);
    });
  }
};

// Điểm cơ bản theo số mạng (chưa tính nhân rainbow):
// ma nhiều mạng có thưởng đặc biệt: 5 -> 50, 10 -> 100, 15 -> 200; còn lại = số mạng
const getLivesReward = (lives: number): number => {
  if (lives >= 15) return 200;
  if (lives >= 10) return 100;
  if (lives >= 5) return 50;
  return lives;
};

function FlyingGhost({
  id,
  top,
  delay = 0,
  size = 50,
  duration = 10,
  direction = 1,
  color = "#FFFFFF",
  lives = 1,
  rainbow = false,
  rainbowMultiplier = 10,
  isGameActive = true,
  onComplete,
  onCatch,
}: FlyingGhostProps) {
  const [isCaught, setIsCaught] = useState(false);
  // Số mạng còn lại: chỉ áp dụng khi đang chơi (focus mode). Ngoài focus mode luôn 1.
  const totalLives = isGameActive ? Math.max(1, lives) : 1;
  // Điểm khi bắt được: điểm cơ bản theo mạng, nhân thêm nếu là ma bóng mờ 7 màu
  const rewardPoints = getLivesReward(totalLives) * (rainbow ? rainbowMultiplier : 1);
  const [hitsLeft, setHitsLeft] = useState<number>(totalLives);
  const [isHitFlash, setIsHitFlash] = useState<boolean>(false);
  const lastCatchTimeRef = useRef<number>(0);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Nguồn "sự thật" cho số mạng còn lại, tránh đọc state cũ (stale) trong closure
  const hitsLeftRef = useRef<number>(totalLives);
  const caughtRef = useRef<boolean>(false);
  // Đã bị người chơi đánh trúng ít nhất 1 lần -> không cho timer/animation tự xoá nữa
  const engagedRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  // Đồng bộ số mạng khi totalLives thay đổi (vd: bật focus mode sau khi ma đã spawn)
  useEffect(() => {
    if (!caughtRef.current) {
      hitsLeftRef.current = totalLives;
      setHitsLeft(totalLives);
    }
  }, [totalLives]);

  useEffect(() => {
    if (isCaught) return;

    const delayMs = typeof delay === "number" ? delay * 1000 : parseFloat(String(delay || "0")) * 1000;
    const durationMs = duration * 1000;
    const startDelay = delayMs < 0 ? 0 : delayMs;
    const remainingDuration = delayMs < 0 ? (durationMs + delayMs) : durationMs;
    // Không cho timer về 0/âm (khi delay âm lớn hơn duration) khiến ma bị xoá ngay lập tức
    const totalTime = Math.max(1000, startDelay + remainingDuration + 500);

    const timer = setTimeout(() => {
      if (id !== undefined && onComplete && !caughtRef.current && !engagedRef.current) {
        onComplete(id);
      }
    }, totalTime);

    return () => clearTimeout(timer);
    // Cố ý bỏ onComplete/isCaught khỏi deps để timer không bị reset mỗi lần click gây re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, delay, duration]);

  const handleAnimationEnd = () => {
    // Ma đã bay hết màn hình -> dọn dẹp (kể cả khi đã bị đánh dở, vì lúc này ma đã ra khỏi màn hình)
    if (caughtRef.current) return;
    if (id !== undefined && onComplete) {
      onComplete(id);
    }
  };

  const handleTriggerCatch = (e: React.SyntheticEvent) => {
    if (!isGameActive) return;

    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
    const now = Date.now();
    if (now - lastCatchTimeRef.current < 200) return;
    lastCatchTimeRef.current = now;

    if (caughtRef.current) return;

    // Người chơi đã tương tác -> khoá không cho timer/animation tự xoá con ma
    engagedRef.current = true;

    const remaining = hitsLeftRef.current - 1;

    if (remaining > 0) {
      // Chưa hết mạng -> giảm 1 mạng, flash cam nhạt rồi trở lại trắng
      hitsLeftRef.current = remaining;
      setHitsLeft(remaining);
      setIsHitFlash(true);
      playHitSound();
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = setTimeout(() => setIsHitFlash(false), 320);
      return;
    }

    // Hết mạng -> bắt được, cộng điểm = tổng số mạng
    hitsLeftRef.current = 0;
    caughtRef.current = true;
    setHitsLeft(0);
    setIsCaught(true);
    if (rainbow) {
      playRainbowCatchSound();
    } else {
      playCatchSound();
    }

    if (id !== undefined) {
      if (onCatch) onCatch(id, rewardPoints);
      // Chờ animation bắt ma + điểm thưởng bay lên xong (0.9s) rồi mới xoá
      setTimeout(() => {
        if (onComplete) onComplete(id);
      }, 900);
    }
  };

  const startLeft = direction === 1 ? "105%" : `-${size + 20}px`;
  const endLeft = direction === 1 ? `-${size + 20}px` : "105%";
  const flipX = direction === 1 ? -1 : 1;

  const ghostStyle: React.CSSProperties = {
    top: typeof top === "number" ? `${top}%` : top,
    width: `${size}px`,
    height: `${size}px`,
    animationDuration: `${duration}s`,
    animationDelay: typeof delay === "number" ? `${delay}s` : delay,
    ["--start-left" as any]: startLeft,
    ["--end-left" as any]: endLeft,
    ["--flip-x" as any]: flipX,
  };

  return (
    <div
      style={ghostStyle}
      className={`flying-ghost ${isGameActive ? "is-catchable cursor-pointer" : ""} ${isCaught ? "is-caught" : ""} ${rainbow ? "is-rainbow" : ""}`}
      onAnimationEnd={handleAnimationEnd}
      onPointerDown={handleTriggerCatch}
    >
      <div className="ghost-content">
        <VectorGhost
          color={isHitFlash ? HIT_COLOR : color}
          isCaught={isCaught}
          isHit={isHitFlash}
          isRainbow={rainbow && !isHitFlash}
        />
        {/* Ống máu màu cam có sọc chia đốt ở dưới chân con ma:
            chỉ khi đang chơi và còn > 1 mạng */}
        {isGameActive && totalLives > 1 && !isCaught && (
          <span className="ghost-health">
            {Array.from({ length: totalLives }, (_, i) => (
              <span
                key={i}
                className={`ghost-health-seg ${i >= hitsLeft ? "is-drained" : ""}`}
              />
            ))}
          </span>
        )}
      </div>

      {/* Điểm thưởng bay lên khi con ma bị bắt (+N = số mạng).
          Đặt ngoài .ghost-content để không bị ảnh hưởng animation co lại khi bắt */}
      {isCaught && (
        <span className={`ghost-reward ${rainbow ? "is-rainbow" : ""}`}>+{rewardPoints}</span>
      )}
    </div>
  );
}

export default React.memo(FlyingGhost);
